import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { mangaList } from '@/mocks/manga';

export default function MangaDetailPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const manga = mangaList.find(m => m.slug === slug);

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该漫画不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors no-underline"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-home-line"></i>
              </span>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const pageTitle = `${manga.title}-${manga.author}-韩漫免费漫画在线-下拉式阅读`;
  const pageDescription = `韩漫之家提供${manga.title}免费在线阅读，${manga.author}作品，${manga.category}类韩国漫画，支持下拉式阅读模式。${manga.description.slice(0, 80)}`;

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.hanmanzhijia.com/' },
          { '@type': 'ListItem', position: 2, name: '分类浏览', item: 'https://www.hanmanzhijia.com/category/all' },
          { '@type': 'ListItem', position: 3, name: manga.title },
        ],
      },
      {
        '@type': 'Article',
        headline: manga.title,
        author: { '@type': 'Person', name: manga.author },
        datePublished: manga.chapters[0]?.updatedAt || manga.updatedAt,
        dateModified: manga.updatedAt,
        description: manga.description,
        mainEntityOfPage: `https://www.hanmanzhijia.com/manga/${manga.slug}`,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb />

        <main className="pb-14">
          <section className="pt-8">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-64 shrink-0">
                  <div className="relative overflow-hidden rounded-xl aspect-[3/4] bg-background-100">
                    <img
                      src={manga.coverImage}
                      alt={`${manga.title} - ${manga.author}韩国漫画`}
                      title={`${manga.title} - 韩漫之家免费漫画`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                    {manga.title}
                  </h1>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-sm text-foreground-600">
                      作者：<strong>{manga.author}</strong>
                    </span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <Link
                      to={`/category/${manga.categorySlug}`}
                      className="text-sm text-primary-500 hover:text-primary-600 font-medium no-underline"
                    >
                      {manga.category}
                    </Link>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                      manga.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {manga.status}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-1">
                      <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                        <i className="ri-star-fill text-sm"></i>
                      </span>
                      <span className="text-lg font-bold text-foreground-900">{manga.rating}</span>
                      <span className="text-xs text-foreground-500">/ 5.0</span>
                    </div>
                    <span className="text-xs text-foreground-400">
                      {(manga.views / 10000).toFixed(0)}万人阅读
                    </span>
                    <span className="text-xs text-foreground-400">
                      共 {manga.chapters.length} 话
                    </span>
                    <span className="text-xs text-foreground-400">
                      最后更新：{manga.updatedAt}
                    </span>
                  </div>

                  <div className="bg-background-100 rounded-xl p-5 mb-6">
                    <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {manga.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <Link
                      to={`/category/${manga.categorySlug}`}
                      className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors no-underline cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-circle-line"></i>
                      </span>
                      开始阅读
                    </Link>
                    <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 text-foreground-500 hover:text-accent-500 transition-colors cursor-pointer">
                      <i className="ri-heart-line text-lg"></i>
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                      <i className="ri-share-forward-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-12">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-6">
                章节列表
              </h2>
              <div className="space-y-2">
                {manga.chapters.map((chapter) => (
                  <a
                    key={chapter.id}
                    href={`#chapter-${chapter.number}`}
                    className="flex items-center justify-between p-4 rounded-xl bg-background-100 hover:bg-primary-50 transition-colors group no-underline"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-primary-100 text-foreground-600 group-hover:text-primary-500 transition-colors text-xs font-semibold">
                        {chapter.number}
                      </span>
                      <div>
                        <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors">
                          第{chapter.number}话：{chapter.title}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-foreground-400">{chapter.updatedAt}</span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <section className="mt-12">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="text-lg font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
                {mangaList
                  .filter(m => m.categorySlug === manga.categorySlug && m.id !== manga.id)
                  .slice(0, 4)
                  .map(related => (
                    <Link
                      key={related.id}
                      to={`/manga/${related.slug}`}
                      className="group no-underline"
                    >
                      <div className="relative overflow-hidden rounded-lg mb-3 aspect-[3/4] bg-background-100">
                        <img
                          src={related.coverImage}
                          alt={`${related.title} - 韩漫在线漫画`}
                          title={`${related.title} - 好看的韩国漫画`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{related.author}</p>
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}